/**
 * frontend/src/components/shared/VoileExport.tsx
 * `#17` RAPPORT PPT — **LE VOILE**, monté UNE FOIS pour toute l'application.
 *
 * ⚠⚠ SPEC HKM DU 29/08, VERBATIM : *« pendant la génération, le dashboard doit
 *   être blurry, aucun clic possible, et pop-up d'avancement visible même quand
 *   on va sur une autre page ! »* — **trois propriétés, et les trois sont
 *   tenues ici.**
 *
 * ⚠ CE N'EST PAS UN HABILLAGE, C'EST UNE GARDE : pendant la séquence, l'écran
 *   **change d'état tout seul** (région, drill, route). *Un geste de
 *   l'utilisateur au milieu changerait un état que l'orchestrateur croit tenir
 *   — les captures suivantes montreraient autre chose que ce que le rapport
 *   annonce, et la restauration remettrait l'écran dans un état qu'il n'a
 *   jamais quitté.*
 */

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Loader2 } from 'lucide-react';
import { useAvancementExport } from '@/stores/voileExportStore';

/**
 * ⚠⚠ **LE FLOU EST VISIBLE, ET C'EST LA MESURE QUI L'A DIT** : le voile portait
 *   `backdrop-blur-[2px]` — *un flou de deux pixels ne se voit pas ; la
 *   propriété existait dans le code sans exister à l'écran.* Hkm demande un
 *   dashboard **flouté**, pas un dashboard techniquement flouté.
 *
 * ⚠ **ET IL NE PEUT PAS ENTRER DANS LES CAPTURES** : `backdrop-filter` n'agit
 *   que sur ce qui est peint **derrière l'élément qui le porte** — il ne modifie
 *   ni le DOM ni les styles des nœuds capturés. *Le voile est un FRÈRE des
 *   pages, jamais un ancêtre : `html-to-image` clone le nœud visé, et le voile
 *   n'est pas dans ce clone.* Il porte en plus `data-export-ignore`.
 */
const CLASSES_VOILE =
  'fixed inset-0 z-[100] flex items-center justify-center '
  + 'bg-black/40 backdrop-blur-md cursor-wait';

/** ⚠ L'élément qui porte l'application — c'est LUI qu'on rend inerte. */
const RACINE_APP = 'root';

export function VoileExport() {
  const avancement = useAvancementExport();
  const enCours = avancement !== null;

  /**
   * ⚠⚠ **AUCUN CLIC, ET AUCUNE TOUCHE.** Le voile couvre la fenêtre, donc la
   *   souris ne peut atteindre personne — *mais un voile qui avale les clics et
   *   laisse tabuler vers un bouton n'avale rien.* Le clavier passait : rien
   *   n'empêchait `Tab` d'atteindre une commande derrière, ni `Entrée` de
   *   l'actionner.
   *
   * ⚠ `inert` retire tout le sous-arbre du parcours de focus **et** de
   *   l'activation — *c'est la seule propriété qui traite les deux d'un même
   *   geste ; un piège à focus écrit à la main aurait laissé les raccourcis.*
   * ⚠ Le voile est en **portail sur `document.body`**, donc **hors** de la
   *   racine rendue inerte : *il reste, lui, parfaitement vivant.*
   */
  useEffect(() => {
    const racine = document.getElementById(RACINE_APP);
    if (!racine) return;
    if (enCours) racine.setAttribute('inert', '');
    else racine.removeAttribute('inert');
    // ⚠ On rend l'écran même si le composant se démonte en cours de route.
    return () => { racine.removeAttribute('inert'); };
  }, [enCours]);

  if (!avancement) return null;

  return createPortal(
    <div
      data-export-ignore=""
      data-testid="voile-export-ppt"
      className={CLASSES_VOILE}
      // ⚠ Ceinture et bretelles : ce qui atteindrait le voile s'y arrête.
      onClickCapture={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onKeyDownCapture={(e) => { e.preventDefault(); e.stopPropagation(); }}
      role="alertdialog"
      aria-live="polite"
      aria-label="Export du rapport en cours"
      tabIndex={-1}
    >
      <div className="rounded-xl border border-[var(--wrapper-contour)] dark:border-surface-dark-border bg-surface-dark-elevated px-6 py-4 text-center">
        <Loader2 className="mx-auto h-5 w-5 animate-spin text-[var(--text-muted)]" />
        {/**
          * ⚠⚠ `#653` — **LE COMPTEUR DIT DE QUOI IL PARLE.** Hkm a lu « 67 » et
          *   compris **67 diapositives** ; le rapport en fait **36**. *Un chiffre
          *   nu se fait toujours interpréter par ce que le lecteur attend.*
          */}
        <p className="mt-2 text-sm font-semibold text-[var(--text-primary)]">
          Export du rapport — {avancement.unite} {avancement.fait}/{avancement.total}
        </p>
        {/* ⚠ L'ÉTAPE EST NOMMÉE : l'écran joue tout seul, et *un écran qui
            s'agite sans dire pourquoi ressemble à une panne.* */}
        <p className="mt-1 text-xs text-[var(--text-muted)]">{avancement.etape}</p>
      </div>
    </div>,
    document.body,
  );
}
